// Customer-facing guide: how to set up batteries in Uptick for the BT-1851 (mirrors UPTICK_BATTERY_GUIDE.md in the
// repo — keep the two in step; uptick_plan.md §12). Linked from the Integrations page and the setup guide, step 5.

import { Link } from "react-router-dom";

const FIELDS: Array<[string, string, string]> = [
  ["Location", "Where the batteries are, for example “FIP cabinet, ground floor plant room”", "Finding them on site"],
  ["Make and Model", "Exactly as printed on the battery, for example AusForce, NT12-26",
    "The BT-1851 picks batteries from the same names, so tester, cloud and Uptick agree"],
  ["Capacity", "The Ah printed on the battery, for example 26", "The BT-1851 measures the delivered capacity against it"],
  ["Quantity and voltage", "For example 2 × 12 V (a 24 V set)", "Which batteries make up the set"],
  ["Installation date", "When this set went in", "Replacement planning"],
  ["Barcode (optional)", "The battery ID from the label", "Scanning the label in the Uptick app finds the set"],
];

const EXAMPLE: Array<[string, string]> = [
  ["Asset type", "Standby Battery Set"],
  ["Parent asset", "Fire Detection System — main FIP"],
  ["Location", "FIP cabinet, ground floor plant room"],
  ["Battery ID (on the label, and the test name)", "B-25CF-63Z1"],
  ["Make / Model", "AusForce / NT12-26"],
  ["Capacity", "26 Ah"],
  ["Quantity", "2 × 12 V (24 V set)"],
  ["Installed", "2024-03-14"],
  ["Barcode (optional)", "B-25CF-63Z1"],
  ["Routine", "Battery test, AS1851-2012, annual"],
];

export function UptickBatteryGuidePage() {
  return (
    <section style={{ maxWidth: "60em" }}>
      <p><Link to="/integrations">← Integrations</Link></p>
      <h2>Batteries in Uptick — recommended setup for the BT-1851</h2>
      <p>
        Until now a fire system&rsquo;s standby batteries rarely needed a record of their own in Uptick: a battery
        size noted on the panel, a replacement date in the notes. A battery tester changes that. Every BT-1851 test
        produces a measured result &mdash; Pass or Fail, the capacity the batteries actually delivered, a PDF report
        &mdash; and that result has to land on the right batteries, visit after visit, so you can see a set losing
        capacity well before it fails. That only works when each set of batteries is its own asset, and carries an ID
        a technician can read on site.
      </p>
      <p>
        This guide is our recommended way to set that up. It complements the{" "}
        <Link to="/integrations/uptick-guide">setup guide</Link>, which connects BT-1851 Cloud to your Uptick tenant
        and creates the questions the results are written to.
      </p>

      <h3>1. One asset per battery set</h3>
      <ul>
        <li>
          <strong>A battery set is the batteries that are tested together and replaced together</strong> &mdash;
          typically the two 12 V batteries in series behind a 24 V fire panel, or the single 12 V battery behind a
          12 V power supply. Make each set one asset. The BT-1851 tests a set in one run and reports one result for it.
        </li>
        <li>
          <strong>Every system with its own batteries gets its own battery set:</strong> the fire panel, the EWIS,
          each booster or power supply, each sub-panel.
        </li>
        <li>
          <strong>Use one asset type for all battery sets, with &ldquo;Battery&rdquo; in its name</strong> (for
          example &ldquo;Standby Battery Set&rdquo;). The BT-1851 prompt set is attached to this type (setup guide,
          step 4), so when you pick where a result goes, BT-1851 Cloud offers exactly these assets (a battery-named
          type is its fallback if no type is attached).
        </li>
        <li>
          <strong>Make each battery set a child of the system it powers</strong> (its parent asset), so it appears
          under that panel or EWIS and the technician finds it there.
        </li>
      </ul>

      <h3>2. The battery ID — the label on the batteries</h3>
      <p>
        Every battery set gets a <strong>battery ID</strong> from the BT-1851, for example B-25CF-63Z1. The tester
        creates it on the set&rsquo;s first test and prints it &mdash; as text and as a barcode &mdash; on the result
        label, and that label is stuck on the batteries. From then on the ID is how the set is recognised: the
        technician enters it on the tester at every test, and BT-1851 Cloud keeps the set&rsquo;s whole test history
        under it.
      </p>
      <ul>
        <li>
          <strong>It is unique and typo-proof.</strong> The last character is a check character: one wrong
          character, or two swapped ones, and the tester says so before the test starts.
        </li>
        <li>
          <strong>It needs nothing in Uptick to exist.</strong> A set can be tested, and its history kept, before it
          has an asset.
        </li>
        <li>
          <strong>It is linked to the Uptick battery set once.</strong> The first result sent to that set&rsquo;s work
          order teaches BT-1851 Cloud the link, or link it yourself on the battery&rsquo;s page in BT-1851 Cloud. After
          that, results with this ID go to that set&rsquo;s work orders.
        </li>
        <li>
          <strong>Your phone is the scanner.</strong> The label&rsquo;s QR code opens the test in BT-1851 Cloud, which
          links to the battery&rsquo;s page: its tests, its Uptick link, and <strong>Link to an Uptick battery
          set</strong> &mdash; so the link can be made on site.
        </li>
        <li>
          <strong>Optional:</strong> put the battery ID in the battery set&rsquo;s <strong>Barcode</strong> field in
          Uptick, and Uptick&rsquo;s own app finds the set when it scans the label.
        </li>
      </ul>

      <h3>3. What to record on each battery set in Uptick</h3>
      <table>
        <thead><tr><th>Field</th><th>What to enter</th><th>Why</th></tr></thead>
        <tbody>
          {FIELDS.map(([f, what, why]) => <tr key={f}><td>{f}</td><td>{what}</td><td>{why}</td></tr>)}
        </tbody>
      </table>
      <p>
        Uptick has no built-in battery fields: record Make, Model, Capacity and Quantity in custom asset fields.
        Many tenants already have Make and Model fields from an earlier import &mdash; reuse those.
      </p>
      <p>
        <strong>When a set is replaced</strong>, keep the Uptick asset &mdash; it stands for the batteries in that
        position &mdash; and update its installation date, and its make, model and capacity if they changed. The new
        batteries get a new battery ID on their first test; link it to the same asset (section 2).
      </p>

      <h3>4. Put every battery set on the routine</h3>
      <p>
        Add each battery set to the property&rsquo;s routine, with the routine service type the BT-1851 prompt set
        is attached to (setup guide, step 4). Every scheduled visit then carries a battery service task for each set
        on its work order &mdash; the line the BT-1851 result is written to. A set that is not on the routine never
        appears on a work order, and its result has nowhere to go.
      </p>

      <h3>5. On site with the BT-1851</h3>
      <p>
        The tester does not need a connection on site. Results are kept on the tester and upload to BT-1851 Cloud by
        themselves the next time it is online (office Wi-Fi, or a phone hotspot); nothing is lost while it is offline.
      </p>
      <ol>
        <li>Find the batteries and their label.</li>
        <li>
          Start the test on the BT-1851. With the work-order list on the tester (it downloads the open battery work
          orders while online), choose the site and the battery set and press Next &mdash; the battery ID and the
          battery are filled in. Otherwise pick the maker and model, and type the battery ID from the label as the test
          name; for a set without a label, press <strong>New battery ID</strong>.
        </li>
        <li>After the test, print the result label. For a new set, stick it on the batteries &mdash; it is their ID label.</li>
        <li>
          Tick the battery line on the work order in Uptick to match the verdict the BT-1851 shows (Pass or Fail),
          and finish the work order as usual. The readings and the report follow.
        </li>
        <li>
          Once the tester is back online:
          <ul>
            <li>
              a test started from the work-order list goes straight to that work order: your tick stays, and the
              readings and the report are added to it;
            </li>
            <li>
              otherwise the result waits under <strong>Awaiting an Uptick work order</strong> on the Integrations page.
              For a linked battery ID, BT-1851 Cloud looks up that set&rsquo;s work order in Uptick &mdash; still open,
              or ticked on site &mdash; and shows it as a suggestion: check the site and the battery set and press{" "}
              <strong>Send to this work order</strong>. It is never sent without that click. For a battery ID that is
              not linked yet, link it once (section 2) or pick the work order on the test.
            </li>
          </ul>
        </li>
      </ol>
      <p>If your tick and the tester disagree, BT-1851 Cloud asks before replacing the tick.</p>

      <h3>6. Moving battery details you already have</h3>
      <p>
        If your battery details sit on the panel today &mdash; a battery size, quantity or date on the fire detection
        or EWIS asset, often from an import &mdash; move them to battery sets. This is a one-off job in Uptick, one
        system at a time; it can also be done property by property ahead of each site&rsquo;s next battery test:
      </p>
      <ol>
        <li>Create a battery set under the system (sections 1 and 3), copying the size, quantity and date last installed.</li>
        <li>Add it to the routine (section 4).</li>
        <li>
          At the next visit, test it with a new battery ID and stick the label on the batteries; the result links the
          ID to the set.
        </li>
      </ol>
      <p>The panel&rsquo;s own battery fields can stay; BT-1851 Cloud does not read them.</p>

      <h3>Example</h3>
      <table>
        <tbody>
          {EXAMPLE.map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}
        </tbody>
      </table>
      <p className="muted">Support: ryp@anjindustries.com.au</p>
    </section>
  );
}
